import {Filter} from 'common/src/components/filters/CLFilter/CLFilter.types';
import apiV2Service from 'common/src/modules/api/api-v2.service';
import apiService from 'common/src/modules/api/api.service';
import {Nodes} from 'common/src/modules/decisionTree/decisionTree.types';
import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import {DateTime} from 'luxon';
import {MetricValue, RemoteOptionalDimensions} from '../analytics/analytics.types';
import extractCustomConversionsFromMetrics from '../analytics/utils/extractCustomConversionsFromMetrics';
import {
  toClientAvailableFilter,
  toClientAvailableMetricsForPreset,
  toClientDetailedExperimentData,
  toClientExperimentsList,
  toClientGeneralStatistics,
  toLocalFlightControlInsights,
  toNodesAggregatedInsights,
} from './campaignAnalytics.dataConverter';
import {
  AvailableMetricsForPreset,
  CampaignInsightsData,
  Experiment,
  InsightMetricsOption,
  RemoteAvailableFilter,
  RemoteAvailableMetricsForPreset,
  RemoteCampaignInsightsData,
  RemoteDetailedExperiment,
  RemoteExperiment,
  MetaCustomConversions,
  RemoteGeneralStatistics,
} from './campaignAnalytics.types';
import {
  toClientOptionalDimensions,
  toClientOptionalDimensionsForFilters,
  toClientOptionalInsightsDimensions,
} from '../analytics/analytics.dataConverters';
import {AvailableMetricWithTotal} from '../campaigns/store/insightsPage/insightsPage.types';

const BASE_PATH = 'CampaignAnalytics';

async function archiveCampaignExperimentsData({
  campaignId,
  experimentId,
  experimentType,
}: {
  campaignId: number;
  experimentId: number;
  experimentType?: string;
}) {
  return await apiService.executeGetRequest('Campaigns/ArchiveCampaignExperimentsData', {
    campaignId,
    experimentType,
    ...(experimentId && {experimentId}),
  });
}

async function getExperimentsList(params: {
  campaignId: number;
  nodeId: number;
  campaignVersionId: number;
}): Promise<Experiment[]> {
  const {data} = await apiService.executeGetRequest<RemoteExperiment[]>(
    'Experiments/GetExperimentsList',
    {...params},
  );

  return toClientExperimentsList(data);
}

async function getDetailedExperimentData(params: {
  campaignId: number;
  experimentId: number;
  campaignVersionId: number;
}) {
  const [
    campaignEnums,
    {
      data: [detailedExperimentData],
    },
  ] = await Promise.all([
    enumsService.loadEnums(CLEnumType.CampaignPage),
    await apiService.executeGetRequest<RemoteDetailedExperiment[]>(
      'Experiments/GetDetailedExperimentData',
      {...params},
    ),
  ]);

  return toClientDetailedExperimentData(detailedExperimentData, campaignEnums);
}

async function getPresets() {
  const {data} = await apiV2Service.executeGetRequest<InsightMetricsOption[]>(
    `${BASE_PATH}/GetPresets`,
  );

  return data;
}

async function addPreset(params: InsightMetricsOption) {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(params.metrics);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${BASE_PATH}/AddPreset`,
    {},
    {...params, metrics, customConversions},
  );

  return data;
}

async function updatePreset(params: InsightMetricsOption) {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(params.metrics);
  const {data} = await apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/UpdatePreset/${params.id}`,
    {},
    {...params, metrics, customConversions},
  );

  return data;
}

async function deletePreset(id: number) {
  return apiV2Service.executeDeleteRequest<void>(`${BASE_PATH}/DeletePreset/${id}`);
}

async function getAvailableFilters({
  metrics,
  isSocial,
  campaignId,
  campaignVersionId,
}: {
  isSocial: boolean;
  metrics: MetricValue[];
  campaignId: number;
  campaignVersionId: number;
}) {
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);
  const {data: dimensions} = await apiV2Service.executePostRequest<RemoteAvailableFilter[]>(
    `${BASE_PATH}/GetAvailableFilters`,
    {},
    {
      metrics: allMetrics,
      customConversions,
      filters: [],
      isSocial,
      campaignId,
      campaignVersionId,
    },
  );

  return toClientAvailableFilter(dimensions);
}

async function getInsights({
  nodes,
  nodesIdsForCampaignInsights,
  campaignId,
  from,
  to,
  nodeIds,
  fallbackCreativeNodeId,
  metrics,
  timezone,
  filters,
}: {
  nodes: Nodes;
  nodesIdsForCampaignInsights: number[];
  campaignId: number;
  from?: DateTime;
  to?: DateTime;
  nodeIds?: number[];
  fallbackCreativeNodeId: number;
  placementsIds?: number[];
  metrics: MetricValue[];
  timezone: string;
  filters: Filter[];
}): Promise<CampaignInsightsData> {
  const params = {campaignId, nodeIds, from: '', to: ''};

  if (from) {
    params.from = from.setZone(timezone, {keepLocalTime: true}).toISO();
  }

  if (to) {
    params.to = to.setZone(timezone, {keepLocalTime: true}).toISO();
  }

  function getFiltersQuery(filters: Filter[]) {
    return _reduce(
      filters,
      (accumulator, filter) => {
        const {filterValue, selectedFilterOptions} = filter;

        return {
          ...accumulator,
          ...(selectedFilterOptions.length && {
            [filterValue]: _map(selectedFilterOptions, 'value'),
          }),
        };
      },
      {},
    );
  }

  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);
  const {data: campaignInsights} =
    await apiV2Service.executePostRequest<RemoteCampaignInsightsData>(
      `${BASE_PATH}/GetAllInsightsV2`,
      {},
      {
        ...params,

        ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
        metrics: allMetrics,
        customConversions,
      },
    );

  const result = toNodesAggregatedInsights({
    nodes,
    nodesIdsForCampaignInsights,
    insights: campaignInsights.insights,
    nodeInsights: campaignInsights.nodeInsights,
    campaignTotals: campaignInsights.totals,
    fallbackCreativeNodeId,
    metrics,
  });

  return {
    totals: toLocalFlightControlInsights(campaignInsights.totals, metrics),
    nodes: result,
  };
}

async function getAvailableMetrics({
  selectedMetrics,
  isSocial,
}: {
  selectedMetrics: MetricValue[];
  isSocial: boolean;
}): Promise<AvailableMetricsForPreset> {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(selectedMetrics);

  const {data} = await apiV2Service.executePostRequest<RemoteAvailableMetricsForPreset>(
    `${BASE_PATH}/GetAvailableMetrics`,
    {},
    {metrics, customConversions, isSocial},
  );

  return toClientAvailableMetricsForPreset(data, selectedMetrics);
}

async function getAvailableFacebookCustomConversions(): Promise<MetaCustomConversions[]> {
  const {data} = await apiV2Service.executeGetRequest<MetaCustomConversions[]>(
    `${BASE_PATH}/GetAvailableFacebookCustomConvertions`,
  );

  return data;
}

async function getOptionalDimensions(campaignId: number, campaignVersionId: number) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetOptionalViewBy`,
    {campaignId, campaignVersionId},
  );

  return toClientOptionalDimensions(data);
}

async function getCampaignOptionalFilters(campaignId: number, campaignVersionId: number) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetCampaignOptionalFilters`,
    {campaignId, campaignVersionId},
  );

  return toClientOptionalDimensionsForFilters(data);
}

async function getCampaignCardSpecificOptionalViewBy({
  campaignId,
  nodeId,
  campaignVersionId,
}: {
  campaignId: number;
  nodeId: number;
  campaignVersionId: number;
}) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/${campaignId}/version/${campaignVersionId}/node/${nodeId}/GetCampaignCardSpecificOptionalViewBy`,
  );

  return toClientOptionalInsightsDimensions(data);
}

async function getGeneralStatistics(campaignId: number): Promise<{
  activeSince: string;
  availableMetricsWithTotals: AvailableMetricWithTotal[];
}> {
  const {data} = await apiV2Service.executeGetRequest<RemoteGeneralStatistics>(
    `${BASE_PATH}/${campaignId}/GeneralStatistics`,
  );

  return {
    activeSince: data.ActiveSince,
    availableMetricsWithTotals: toClientGeneralStatistics(data),
  };
}

export default {
  getInsights,
  getExperimentsList,
  getDetailedExperimentData,
  archiveCampaignExperimentsData,
  addPreset,
  getPresets,
  updatePreset,
  deletePreset,
  getAvailableFilters,
  getAvailableMetrics,
  getAvailableFacebookCustomConversions,
  getOptionalDimensions,
  getCampaignOptionalFilters,
  getCampaignCardSpecificOptionalViewBy,
  getGeneralStatistics,
};
